// Text that came over from the old Kehila system carries two artefacts the
// importer left in the database: HTML-escaped characters that were never
// decoded (&#39;, &#34;, &amp;) and U+FFFD replacement characters where the
// original file-name encoding was lost. Neither can be repaired from the web
// app, so the text is cleaned wherever it is displayed or used as a download
// name.

const NAMED_ENTITIES: { [name: string]: string } = {
  amp: '&',
  quot: '"',
  apos: "'",
  lt: '<',
  gt: '>',
  nbsp: ' ',
}

const decodeHtmlEntities = (text: string) =>
  text.replace(/&(#[xX][0-9a-fA-F]+|#\d+|[a-zA-Z][a-zA-Z0-9]*);/g, (match, token: string) => {
    if (token.charAt(0) === '#') {
      const isHex = token.charAt(1) === 'x' || token.charAt(1) === 'X'
      const code = parseInt(isHex ? token.slice(2) : token.slice(1), isHex ? 16 : 10)
      // Leave anything out of range as-is rather than throwing on it.
      if (!isFinite(code) || code <= 0 || code > 0x10FFFF) return match
      return String.fromCodePoint(code)
    }
    const named = NAMED_ENTITIES[token.toLowerCase()]
    return named !== undefined ? named : match
  })

// Zero-width and bidi control characters: Excel and the old Kehila exporter
// both leave them in file names. They are invisible, so a name looks right
// while an extension test against it fails and a saved file carries them in
// its name. trim() does not remove them - they are not whitespace.
const INVISIBLE_MARKS = /[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g

export const cleanImportedText = (text?: string | null): string => {
  if (!text) return ''
  return decodeHtmlEntities(text)
    .replace(/�+/g, '')
    .replace(INVISIBLE_MARKS, '')
    .replace(/[ \t]{2,}/g, ' ')
    .trim()
}

// Protocol descriptions that came from the old Kehila system are sometimes
// stored as HTML fragments (<p>, <br>, lists), while the app renders and edits
// them as plain text. Convert the markup to line breaks so the text reads
// correctly; the caller renders it with `white-space: pre-wrap` and
// `unicode-bidi: plaintext`, which gives each line its own base direction and
// so keeps mixed Hebrew/English content correctly aligned.

const looksLikeHtml = (text: string) => /<\/?[a-z][^>]*>/i.test(text)

export const htmlToPlainText = (text?: string | null): string => {
  if (!text) return ''
  if (!looksLikeHtml(text)) return cleanImportedText(text)

  const withBreaks = text
    // Drop anything that is not content at all.
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    // Block boundaries become line breaks; paragraphs get a blank line.
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|h[1-6]|blockquote)\s*>/gi, '\n\n')
    .replace(/<\/(div|tr|ul|ol|table|section|article)\s*>/gi, '\n')
    // <li> opens its own line, so </li> would only add a blank one.
    .replace(/<\/li\s*>/gi, '')
    .replace(/<li\b[^>]*>/gi, '\n• ')
    .replace(/<(p|div|tr|h[1-6])\b[^>]*>/gi, '\n')
    // Whatever markup is left carries no text.
    .replace(/<[^>]+>/g, '')

  return cleanImportedText(withBreaks)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

// A protocol file keeps its extension out of its name: the API returns
// fileName "סעיף 2" with fileFormat "pdf". A name with no extension tells the
// browser nothing - it cannot choose a viewer, so it saves the file instead of
// opening it, and what it saves has no extension either. Put the two back
// together. Older records already carry the extension in the name, so it is
// only added when it is missing.
export const protocolFileName = (
  file?: { fileName?: string | null; fileFormat?: string | null } | null
): string => {
  const name = cleanImportedText(file?.fileName)
  const format = cleanImportedText(file?.fileFormat).replace(/^\./, '').toLowerCase()

  if (!name || !format) return name
  return name.toLowerCase().endsWith(`.${format}`) ? name : `${name}.${format}`
}
